import { Box, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";

export type SwitchPlayerMeterProps = {
  data: any;
  silenceDB: number;
  audioChannelCounts: any[];
  highWaterMarks: any;
  setHighWaterMarks: any;
  audioPlaybackChannelCount: number;
};

const SwitchPlayerMeter: React.FC<SwitchPlayerMeterProps> = (props) => {
  const { data, silenceDB, audioChannelCounts, audioPlaybackChannelCount, highWaterMarks, setHighWaterMarks } = props;
  const theme = useTheme();
  const mode = theme.palette.mode;
  const canvasRef = useRef<any>(undefined);

  function drawScale(x, top, width, height) {
    const meterCanvas = canvasRef.current;
    const meterContext = meterCanvas.getContext("2d");
    var tickWidth = 8;

    meterContext.fillStyle = mode === "dark" ? "white" : "black";
    meterContext.font = "Inter 12pt";
    meterContext.textAlign = "center";

    meterContext.fillRect(x + width, top, 1, height);

    for (var step = 0; step >= silenceDB; step -= 6) {
      var y = top + height * (step / silenceDB);

      if ([0, -18, silenceDB].includes(step)) {
        meterContext.fillRect(x + (width - tickWidth), y, tickWidth, 1);
        meterContext.fillText(step, x + (width - tickWidth) / 2, y + 3);
      }
    }
  }

  function drawMeter(rmsDB, highMarkDB, bottomDB, label, x, top, width, height, gradient) {
    const meterCanvas = canvasRef.current;
    const meterContext = meterCanvas.getContext("2d");
    // Ensure that we don't draw below the meter.
    if (rmsDB < silenceDB) rmsDB = silenceDB;
    if (highMarkDB < silenceDB) highMarkDB = silenceDB;
    if (bottomDB < silenceDB) bottomDB = silenceDB;

    // Fill the meter background.
    meterContext.fillStyle = mode === "dark" ? "black" : "white";
    meterContext.fillRect(x, top, width, height);

    // Draw the value rectangle.
    meterContext.fillStyle = gradient;
    var y = top + height * (rmsDB / silenceDB);
    var valueHeight = height * (bottomDB / silenceDB) - height * (rmsDB / silenceDB);
    meterContext.fillRect(x, y, width, valueHeight);

    // Frame the meter.
    meterContext.fillStyle = mode === "dark" ? "black" : "white";
    meterContext.strokeRect(x, top, width, height);

    // Draw the label.
    meterContext.textAlign = "center";
    meterContext.fillStyle = mode === "dark" ? "white" : "black";
    meterContext.fillText(label, x + width / 2, meterCanvas.height);

    // Draw the value.
    meterContext.fillText((Math.round(rmsDB * 100) / 100).toFixed(1), x + width / 2, 10);

    // Draw the high mark line.
    var highMarkDB_Y = top + height * (highMarkDB / silenceDB);
    meterContext.fillRect(x, highMarkDB_Y, width, 1);
  }

  useEffect(() => {
    if (!data) return;

    const meterCanvas = canvasRef.current;
    const meterContext = meterCanvas?.getContext("2d");
    var meterWidth = 10;
    var meterHeight = meterCanvas.height * 0.83;
    var marginWidth = 17;
    var topMargin = 10;
    var x = meterWidth + marginWidth;
    var channelData = data["channel"];
    var channelIndex = 1;
    var trackData = data["track"];
    var trackIndex = 1;
    var trackLabel = "A";

    if (channelData == null || channelData.length < 1) return;

    // Erase the view.
    meterContext.fillStyle = theme.palette.background.paper;
    meterContext.fillRect(0, 0, meterCanvas.width, meterCanvas.height);

    // Draw the scale.
    drawScale(5, topMargin, meterWidth, meterHeight);

    // Create the audio meter gradient.
    var meterGradient = meterContext.createLinearGradient(0, topMargin, 0, meterHeight);
    meterGradient.addColorStop(0, "red");
    meterGradient.addColorStop(-6 / silenceDB - 0.01, "red");
    meterGradient.addColorStop(-6 / silenceDB + 0.01, "yellow");
    meterGradient.addColorStop(-12 / silenceDB - 0.01, "yellow");
    meterGradient.addColorStop(-12 / silenceDB + 0.01, "green");
    meterGradient.addColorStop(1, "green");

    // Draw the channel data.
    let count = 0;
    for (var index in channelData) {
      if (count >= audioPlaybackChannelCount) break;
      count += 1;
      var rmsDB = channelData[index]["rms"];
      var peak = channelData[index]["peak"];

      // Update the high-water mark.
      if (highWaterMarks.length < parseInt(index) + 1)
        setHighWaterMarks(Array.apply(null, Array(parseInt(index) + 1)).map(function () {}));
      if (highWaterMarks[index] == null || highWaterMarks[index] < rmsDB)
        setHighWaterMarks((x) => x.map((a, i) => (i === parseInt(index) ? rmsDB : a)));

      // If this is a new track, skip a space.
      var trackIndexForThisChannel = 0;
      var totalChannelsForPreviousTracks = 0;
      for (trackIndex = 1; trackIndex < audioChannelCounts.length; trackIndex++) {
        totalChannelsForPreviousTracks += audioChannelCounts[trackIndex - 1];
        var currentTrackChannelCount = audioChannelCounts[trackIndex];

        if (channelIndex <= totalChannelsForPreviousTracks) break;
        else if (
          channelIndex > totalChannelsForPreviousTracks &&
          channelIndex <= totalChannelsForPreviousTracks + currentTrackChannelCount
        ) {
          trackIndexForThisChannel = trackIndex;

          if (channelIndex == totalChannelsForPreviousTracks + 1) x += meterWidth + marginWidth;

          break;
        }
      }

      drawMeter(
        rmsDB,
        highWaterMarks[index],
        silenceDB,
        channelIndex,
        x,
        topMargin,
        meterWidth,
        meterHeight,
        meterGradient
      );

      x += meterWidth + marginWidth;
      channelIndex++;
    }

    // Skip a space to separate the channel data from the loudness data.
    x += meterWidth + marginWidth;

    // Draw the scale again.
    drawScale(x, topMargin, meterWidth, meterHeight);
    x += meterWidth + marginWidth;

    //Draw the track loudness data.
    for (var index in trackData) {
      var rmsDB = trackData[index]["momentary"]["rms"];
      var peak = trackData[index]["momentary"]["peak"];
      drawMeter(peak, silenceDB, silenceDB, trackLabel + " (M)", x, topMargin, meterWidth, meterHeight, meterGradient);
      x += meterWidth + marginWidth;

      rmsDB = trackData[index]["short term"]["rms"];
      peak = trackData[index]["short term"]["peak"];
      drawMeter(peak, silenceDB, silenceDB, trackLabel + " (S)", x, topMargin, meterWidth, meterHeight, meterGradient);
      x += meterWidth + marginWidth;

      rmsDB = trackData[index]["integrated"]["rms"];
      peak = trackData[index]["integrated"]["peak"];
      drawMeter(peak, silenceDB, silenceDB, trackLabel + " (I)", x, topMargin, meterWidth, meterHeight, meterGradient);
      x += meterWidth + marginWidth;

      rmsDB = trackData[index]["range"]["rms"];
      peak = trackData[index]["range"]["peak"];
      drawMeter(peak, peak, rmsDB, trackLabel + " (LRA)", x, topMargin, meterWidth, meterHeight, meterGradient);
      x += meterWidth + marginWidth;

      // Skip a space to separate the tracks.
      x += meterWidth + marginWidth;

      trackLabel = String.fromCharCode(trackLabel.charCodeAt(0) + 1);
    }
  }, [data]);
  return (
    <Box
      sx={{ backgroundColor: theme.palette.background.paper }}
      width={"100%"}
      height={"232px"}
      alignItems={"center"}
      alignContent={"center"}
      display={"flex"}
      justifyContent={"center"}
      flexDirection={"row"}
    >
      <canvas
        ref={canvasRef}
        id="meters"
        width={"650"}
        height={"200"}
        style={{ backgroundColor: theme.palette.background.paper }}
      ></canvas>
    </Box>
  );
};

export default SwitchPlayerMeter;
